import { getEnv } from "src/lib/env";
import createClient from "openapi-fetch";
import { paths } from "src/strapi/schema";
import { hasMediaOptimizedFormats } from "src/strapi/hasMediaOptimizedFormats";
import { notFound } from "@tanstack/react-router";
import qs from "qs";

const populateAllQuery = {
  populate: "*",
};

export class StrapiRepository {
  private readonly strapiClient;
  private readonly strapiUrl;

  constructor() {
    const strapiUrl = getEnv("VITE_STRAPI_PUBLIC_URL");
    if (!strapiUrl) {
      throw new Error("Strapi url is not defined");
    }
    this.strapiUrl = strapiUrl;
    this.strapiClient = createClient<paths>({
      baseUrl: `${strapiUrl}/api`,
      querySerializer: (queryParams) => {
        const { sort, ...rest } = queryParams;
        let result = qs.stringify(rest, { arrayFormat: "brackets" });
        if (sort) {
          result += `${result ? "&" : ""}${qs.stringify({ sort }, { encodeValuesOnly: true })}`;
        }
        return result;
      },
    });
  }

  getNavbarItems = async () => {
    const response = await this.strapiClient.GET("/navbar", {
      params: {
        query: {
          // @ts-ignore
          populate: {
            nav_items: populateAllQuery,
          },
        },
      },
    });

    if (response.error) {
      throw new Error(response.error.error.name);
    }

    return response.data.data;
  };

  getHomepage = async () => {
    const response = await this.strapiClient.GET("/homepage", {
      params: {
        query: {
          // @ts-ignore
          populate: {
            hero_photos: populateAllQuery,
            services: populateAllQuery,
            contacts: populateAllQuery,
            social_links: populateAllQuery,
          },
        },
      },
    });

    if (response.error) {
      throw new Error(response.error.error.name);
    }

    return response.data.data;
  };

  private getMediaUrl = (filePath?: string, provider?: string) => {
    if (!filePath) {
      return "";
    }

    if (provider === "local") {
      const strapiPublicUrl = import.meta.env.VITE_STRAPI_PUBLIC_URL;

      return `${strapiPublicUrl}${filePath}`;
    }

    return filePath;
  };

  getImageUrl = (
    image: Parameters<typeof hasMediaOptimizedFormats>[0],
    preferredFormat: "large" | "medium" | "small" | "thumbnail" = "large",
  ) => {
    const hasOptimizedFormats = hasMediaOptimizedFormats(image);

    if (hasOptimizedFormats) {
      return this.getMediaUrl(
        image.formats[preferredFormat].url,
        image.provider,
      );
    }

    return this.getMediaUrl(image.url, image.provider);
  };

  getReferences = async (query?: Record<string, any>) => {
    const response = await this.strapiClient.GET("/references", {
      params: {
        query: {
          ...query,
          // @ts-ignore
          pLevel: 2,
        },
      },
    });

    if (response.error) {
      throw new Error(response.error.error.name);
    }

    return response.data.data;
  };

  getContacts = async () => {
    const response = await this.strapiClient.GET("/contact-page", {
      params: {
        query: {
          // @ts-ignore
          pLevel: 4,
        },
      },
    });

    if (response.error) {
      throw new Error(response.error.error.name);
    }

    return response.data.data;
  };

  getReferenceBySlug = async (
    referenceSlug: string,
    status?: "draft" | "published",
  ) => {
    const response = await this.strapiClient.GET("/references", {
      params: {
        query: {
          // @ts-ignore
          pLevel: 2,
          ...(status ? { status } : {}),
          filters: {
            slug: {
              $eq: referenceSlug,
            },
          },
        },
      },
    });

    const firstFoundPage = response.data?.data?.[0];

    if (!firstFoundPage) {
      throw notFound();
    }

    return firstFoundPage;
  };

  getCategoriesWithReferences = async () => {
    const response = await this.strapiClient.GET("/categories", {
      params: {
        query: {
          // @ts-ignore
          fields: ["name", "slug", "id"],
          filters: {
            references: {
              id: {
                $notNull: true,
              },
              isFinished: {
                $eq: true,
              },
            },
          },
          sort: "id:asc",
        },
      },
    });

    if (!response.data) {
      throw new Error("No data for categories");
    }

    return response.data.data;
  };

  getCategoryBySlug = async (categorySlug: string) => {
    const response = await this.strapiClient.GET("/categories", {
      params: {
        query: {
          filters: {
            slug: {
              $eq: categorySlug,
            },
          },
        },
      },
    });

    const firstFoundPage = response.data?.data?.[0];

    if (!firstFoundPage) {
      throw notFound();
    }

    return firstFoundPage;
  };

  getPageBySlug = async (pageSlug: string) => {
    const response = await this.strapiClient.GET("/pages", {
      params: {
        query: {
          // @ts-ignore
          pLevel: 4,
          filters: {
            slug: {
              $eq: pageSlug,
            },
          },
        },
      },
    });

    const firstFoundPage = response.data?.data?.[0];

    if (!firstFoundPage) {
      throw notFound();
    }

    return firstFoundPage;
  };

  getServices = async () => {
    const response = await this.strapiClient.GET("/servicepage", {
      params: {
        query: {
          // @ts-ignore
          pLevel: 4,
        },
      },
    });

    if (response.error) {
      throw new Error(response.error.error.name);
    }

    if (!response.data) {
      notFound();
    }

    return response.data.data;
  };

  postInquiry = async (data: {
    name: string;
    email: string;
    phone: string;
    message: string;
    files: File[];
  }) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("email", data.email);
    formData.append("phone", data.phone);
    formData.append("message", data.message);

    data.files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await this.strapiClient.POST("/contact-page/inquiry", {
      body: formData as any,
    });

    if (response.error) {
      throw new Error(response.error);
    }

    return response.data;
  };
}

export const strapiRepository = new StrapiRepository();
